import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import ImageUploadComponent from '~/components/organism/ImageUploadComponent';
import useImageAssets from '~/hooks/useImageAssets';
import { Heading4 } from '~/components/atom/Typography';
import JustificationContainer from '~/components/atom/JustificationContainer';
import Tabs, { type Props as TabsProps } from '../Tabs';
import TabBody from '../Tabs/components/TabBody';
import { isNil } from 'ramda';
import type { BaseColor } from '~/theme/System/tokens';
import TextButton from '~/components/atom/TextButton';
import useTooltipLayer from '~/hooks/useTooltipLayer';
import ImageCard, { IMAGE_CARD_SIZE } from '~/components/atom/ImageCard';
import NoAssetsMessageContainer from './components/NoAssetsMessageContainer';
import InitialAssetCard from './components/InitialAssetCard';
import Div from '~/components/atom/Div';
import type { UnionImage } from '~/graphql/types';
import useImageUpload from '~/hooks/useImageUpload';

enum Tab {
  Default = '0',
  Asset = '1',
}

const text = {
  tooltipMessage: 'Ga naar Huisstijl om een nieuwe asset toe te voegen',
  tabLabels: {
    default: 'Bestand',
    asset: 'Asset',
  },
};

export type Props = {
  dataTestId?: string;
  className?: string;

  /** Saved image */
  initialImage: UnionImage | null | undefined;

  /** Currently active image */
  currentImage: UnionImage | null | undefined;

  /** Used to upload the image to the s3 bucket */
  imageFileName: string;

  /** Should the container be vertical or horizontal */
  containerDirection?: 'row' | 'column';

  /** If true, the image can be deleted */
  allowDeletion?: boolean;

  onParentUpdate: (args: UnionImage | null) => void;
};

const ImageOrAssetUploadComponent: React.FCC<Props> = ({
  dataTestId,
  className,
  initialImage,
  currentImage,
  imageFileName,
  allowDeletion,
  containerDirection = 'row',
  onParentUpdate,
}) => {
  const initialAssetId =
    initialImage?.__typename === 'AssetImage' ? initialImage.assetId : null;

  const currentAssetId =
    currentImage?.__typename === 'AssetImage' ? currentImage.assetId : null;

  const tooltipProps = useTooltipLayer({
    tooltipMessage: text.tooltipMessage,
  });
  const assets = useImageAssets();

  const [activeTab, setActiveTab] = useState<Tab | null>(null);

  const image = useImageUpload({
    initialUrl:
      currentImage?.__typename === 'DHImage' ? currentImage.url : null,
    s3Key: currentImage?.__typename === 'DHImage' ? currentImage.s3key : null,
    onUpload: ({ s3Key, url }) => {
      onParentUpdate({ __typename: 'DHImage', s3key: s3Key, url });
    },
    onClear: () => {
      onParentUpdate(null);
    },
  });

  const tabWithSavedValue = (() => {
    if (initialAssetId || currentAssetId) return Tab.Asset;
    if (image.url) return Tab.Default;
    return null;
  })();

  useEffect(() => {
    setActiveTab(tabWithSavedValue || Tab.Default);
  }, [tabWithSavedValue]);

  const initialAsset = assets.find(({ id }) => id === initialAssetId);
  const assetMissing =
    activeTab === Tab.Asset && !isNil(initialAssetId) && isNil(initialAsset);

  const remainingAssets = assets
    .filter(({ id }) => id !== initialAssetId)
    .sort((a, b) => a.label.localeCompare(b.label));

  const tabOptions: TabsProps['tabOptions'] = [
    {
      hasValue: tabWithSavedValue === Tab.Default,
    },
    {
      hasValue: tabWithSavedValue === Tab.Asset,
      hasError: assetMissing,
    },
  ];

  const onAssetChange = ({ assetId }: { assetId: string }) => {
    onParentUpdate({ __typename: 'AssetImage', assetId });
  };

  return (
    <Container
      data-testid={dataTestId}
      className={className}
      $color={assetMissing ? 'danger' : 'tertiary'}
    >
      <StyledTextButton
        icon="externalLink"
        size="small"
        onClick={() => window.open(`/-/settings/brand`, '_blank')}
        {...tooltipProps}
      />
      <Tabs tabOptions={tabOptions}>
        <TabBody label={<TabLabel label={text.tabLabels.default} />}>
          <ImageUploadComponent
            direction={containerDirection}
            onDelete={allowDeletion ? image.clear : undefined}
            uploading={image.uploading}
            imageUrl={image.url}
            onUpload={file =>
              void image.upload({ file: file[0], filename: imageFileName })
            }
          />
        </TabBody>
        <TabBody label={<TabLabel label={text.tabLabels.asset} />}>
          <JustificationContainer direction="column" width="100%">
            <NoAssetsMessageContainer />

            <AssetsContainer padding={assets.length > 0 ? ['xxs'] : [null]}>
              <InitialAssetCard
                asset={initialAsset || null}
                currentAssetId={currentAssetId}
                initialAssetId={initialAssetId}
                onChangeAssetId={onAssetChange}
              />
              {remainingAssets.map(asset => (
                <ImageCard
                  key={asset.id}
                  src={asset.url}
                  label={asset.label}
                  onClick={() => onAssetChange({ assetId: asset.id })}
                  selected={currentAssetId === asset.id}
                />
              ))}
            </AssetsContainer>
          </JustificationContainer>
        </TabBody>
      </Tabs>
    </Container>
  );
};

const TabLabel: React.FCC<{ label: string }> = ({ label }) => (
  <JustificationContainer align="center" justification="center" gap="xxs">
    <Heading4 size="base" margin={[null]} color={{ group: 'primary' }}>
      {label}
    </Heading4>
  </JustificationContainer>
);

const Container = styled.div<{ $color: BaseColor }>(
  ({ theme, $color }) => css`
    width: 100%;
    border: ${theme.getTokens().border.width.s} solid ${theme.color($color)};
    padding: ${theme.space('s')};
    border-radius: ${theme.getTokens().border.radius.base};
    position: relative;
  `,
);

const AssetsContainer = styled(Div)(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(auto-fill, ${IMAGE_CARD_SIZE}px);
    grid-auto-rows: ${IMAGE_CARD_SIZE}px;
    gap: ${theme.space('base')};
    width: 100%;
    max-height: 400px;
    overflow-y: scroll;
  `,
);

const StyledTextButton = styled(TextButton)(
  ({ theme }) => css`
    position: absolute;
    z-index: 1;
    top: ${theme.space('base')};
    right: ${theme.space('s')};
  `,
);

export default ImageOrAssetUploadComponent;
